import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { config, validateEnvironment } from '../config/environment';
import { BusinessUser, UpdateBusinessUserRequest, PlaceBusinessInfo, UpdatePlaceBusinessInfoRequest } from '../types/auth';
import { Category, Characteristic, CreateCategoryDto, UpdateCategoryDto, CreateCharacteristicDto, UpdateCharacteristicDto } from '../types/categories';
import { CoffeeProfileLevel, CreateCoffeeProfileLevelDto, UpdateCoffeeProfileLevelDto } from '../types/coffeeProfileLevels';

// Validar ambiente na inicialização
validateEnvironment();

// Tipos para a API
export interface LoginCodeRequest {
  email: string;
}

export interface VerifyCodeRequest {
  contact: string;
  code: string;
}

export interface AdminUser {
  id: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  userType?: 'place_admin' | 'business_admin';
  isSystemSuperAdmin?: boolean;
  businessUserRole?: 'CafeOwner' | 'SystemSuperAdmin' | 'SystemAdmin' | 'LegalAdmin' | 'SupportAdmin';
}

export interface AdminPlace {
  id: string;
  name: string;
  city: string;
  state: string;
  role: string;
}

export interface AdminLoginResponse {
  user: AdminUser;
  places: AdminPlace[];
  expiresAt: string;
}

export interface City {
  id: string;
  name: string;
  stateId: string;
  stateName?: string;
  latitude: number;
  longitude: number;
  status: number;
}

export interface State {
  id: string;
  name: string;
}

// Variáveis para controle do refresh token
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Configuração base do axios
const apiClient: AxiosInstance = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  withCredentials: true, // Importante para cookies HTTP-only
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token automaticamente (se necessário)
apiClient.interceptors.request.use(
  (config) => {
    const locale = typeof navigator !== 'undefined' ? navigator.language?.split('-')[0] : 'pt';
    config.headers['Accept-Language'] = locale || 'pt';
    // Para requisições que precisam de token, ele virá automaticamente via cookie
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação e refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Evitar loop infinito - se a requisição original já é de refresh, não tentar novamente
      if (originalRequest.url?.includes('/admin/auth/refresh')) {
        console.log('Refresh request failed with 401, redirecting to login');
        clearAxiosCookies();
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Se já está fazendo refresh, adicionar à fila
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
        .then(() => {
          return apiClient(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Tentar fazer refresh do token
        // Como estamos usando cookies HTTP-only, não precisamos enviar o refresh token no body
        // O servidor irá ler o cookie automaticamente
        console.log('Tentando fazer refresh do token...');
        console.log('Cookies atuais:', document.cookie);
        
        const response = await apiClient.post('/admin/auth/refresh');
        
        console.log('Refresh response:', response.data);
        
        if (response.data.accessToken) {
          // Atualizar o token no header (se necessário)
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
          
          // Processar fila de requisições pendentes
          processQueue(null, response.data.accessToken);
          
          // Retry da requisição original
          return apiClient(originalRequest);
        } else {
          throw new Error('No access token received');
        }
      } catch (refreshError: any) {
        console.log('Refresh token failed:', refreshError.response?.data || refreshError.message);
        console.log('Refresh error status:', refreshError.response?.status);
        console.log('Refresh error details:', refreshError);
        
        // Se o refresh falhar, limpar dados de autenticação e redirecionar
        processQueue(refreshError, null);
        
        // Limpar cookies e dados locais
        clearAxiosCookies();
        
        // Disparar evento para redirecionar para login
        console.log('Dispatching auth:token-expired event');
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        
        // Fallback: se o evento não funcionar, redirecionar diretamente
        setTimeout(() => {
          if (window.location.pathname !== '/token-expired') {
            console.log('Fallback: redirecting to token-expired page');
            window.location.href = '/token-expired';
          }
        }, 100);
        
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);



// Função para limpar cookies do axios
export const clearAxiosCookies = () => {
  // Limpar cookies do axios
  apiClient.defaults.headers.common['Cookie'] = '';
  apiClient.defaults.headers.common['Authorization'] = '';
};

// Métodos de autenticação
export const authService = {
  // Solicitar código OTP
  requestLoginCode: async (email: string, retryCount = 0): Promise<void> => {
    try {
      await apiClient.post('/admin/auth/request-code', { email });
    } catch (error: any) {
      // Se for timeout e ainda não tentou 2 vezes, tenta novamente
      if ((error.code === 'ECONNABORTED' || error.message?.includes('timeout')) && retryCount < 2) {
        console.log(`Tentativa ${retryCount + 1} falhou, tentando novamente...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Delay crescente
        return authService.requestLoginCode(email, retryCount + 1);
      }
      throw error;
    }
  },

  // Verificar código OTP
  verifyLoginCode: async (contact: string, code: string): Promise<AdminLoginResponse> => {
    const response: AxiosResponse<AdminLoginResponse> = await apiClient.post('/admin/auth/verify-code', {
      contact,
      code,
    });
    return response.data;
  },

  // Validar sessão atual
  validateSession: async (): Promise<void> => {
    await apiClient.get('/admin/auth/validate');
  },

  // Debug authentication
  debugAuth: async (): Promise<any> => {
    const response = await apiClient.get('/admin/auth/debug');
    return response.data;
  },

  // Refresh token
  refreshToken: async (): Promise<any> => {
    const response = await apiClient.post('/admin/auth/refresh');
    return response.data;
  },

  // Logout
  logout: async (): Promise<void> => {
    await apiClient.post('/admin/auth/logout');
  },
};

// Métodos para dados da cafeteria
export const placeService = {
  // Buscar informações da cafeteria atual
  getCurrentPlace: async (placeId: string) => {
    const response = await apiClient.get(`/admin/places/${placeId}`);
    return response.data;
  },

  // Atualizar informações da cafeteria
  updatePlace: async (placeId: string, data: any) => {
    const response = await apiClient.put(`/admin/places/${placeId}`, data);
    return response.data;
  },

  // ===== NOVOS MÉTODOS PARA SELEÇÃO DE CAFETERIA =====
  
  // Buscar cafeterias acessíveis ao usuário
  getAccessiblePlaces: async () => {
    const response = await apiClient.get('/admin/cafeteria-selection/accessible-places');
    return response.data;
  },

  getCities: async (): Promise<City[]> => {
    const response = await apiClient.get('/places/cities');
    return response.data;
  },

  getStates: async (): Promise<State[]> => {
    const response = await apiClient.get('/places/states');
    return response.data;
  },

  // Validar acesso a uma cafeteria específica
  validatePlaceAccess: async (placeId: string) => {
    const response = await apiClient.get(`/admin/cafeteria-selection/validate-access/${placeId}`);
    return response.data;
  },

  // Obter detalhes de uma cafeteria
  getPlaceDetails: async (placeId: string) => {
    const response = await apiClient.get(`/admin/cafeteria-selection/place/${placeId}`);
    return response.data;
  },

  // Obter estatísticas de uma cafeteria
  getPlaceStats: async (placeId: string) => {
    const response = await apiClient.get(`/admin/cafeteria-selection/place/${placeId}/stats`);
    return response.data;
  },

  // ===== MÉTODOS PARA CATEGORIAS E CARACTERÍSTICAS =====
  
  // Categorias
  getCategories: async (): Promise<Category[]> => {
    const response = await apiClient.get('/categories/all');
    return response.data;
  },

  getPlaceCategories: async (placeId: string): Promise<Category[]> => {
    const response = await apiClient.get(`/admin/places/${placeId}/categories`);
    return response.data;
  },

  updatePlaceCategories: async (placeId: string, categoryIds: string[]): Promise<void> => {
    await apiClient.put(`/admin/places/${placeId}/categories`, { categoryIds });
  },

  // Características
  getCharacteristics: async (): Promise<Characteristic[]> => {
    const response = await apiClient.get('/characteristics/all');
    return response.data;
  },

  getPlaceCharacteristics: async (placeId: string): Promise<Characteristic[]> => {
    const response = await apiClient.get(`/admin/places/${placeId}/characteristics`);
    return response.data;
  },

  updatePlaceCharacteristics: async (placeId: string, characteristicIds: string[]): Promise<void> => {
    await apiClient.put(`/admin/places/${placeId}/characteristics`, { characteristicIds });
  },
};

// Métodos para produtos
export const productService = {
  // Listar produtos da cafeteria
  getProducts: async (placeId: string) => {
    const response = await apiClient.get(`/admin/places/${placeId}/products`);
    return response.data;
  },

  // Criar produto
  createProduct: async (placeId: string, data: any) => {
    const response = await apiClient.post(`/admin/places/${placeId}/products`, data);
    return response.data;
  },

  // Atualizar produto
  updateProduct: async (placeId: string, productId: string, data: any) => {
    const response = await apiClient.put(`/admin/places/${placeId}/products/${productId}`, data);
    return response.data;
  },

  // Deletar produto
  deleteProduct: async (placeId: string, productId: string) => {
    await apiClient.delete(`/admin/places/${placeId}/products/${productId}`);
  },

  // Listar produtos com desconto da cafeteria
  getDiscountedProducts: async (placeId: string) => {
    const response = await apiClient.get(`/admin/places/${placeId}/products`);
    return response.data;
  },

  // Criar produto com desconto
  createDiscountedProduct: async (placeId: string, data: any) => {
    const response = await apiClient.post(`/admin/places/${placeId}/products`, data);
    return response.data;
  },

  // Atualizar produto com desconto
  updateDiscountedProduct: async (placeId: string, productId: string, data: any) => {
    const response = await apiClient.put(`/admin/places/${placeId}/products/${productId}`, data);
    return response.data;
  },

  // Deletar produto com desconto
  deleteDiscountedProduct: async (placeId: string, productId: string) => {
    await apiClient.delete(`/admin/places/${placeId}/products/${productId}`);
  },
};

// Métodos para avaliações
export const reviewService = {
  // Listar avaliações da cafeteria com fotos
  getReviews: async (placeId: string, filters?: { rating?: number; skip?: number; take?: number }) => {
    const limit = filters?.take || 10;
    const skip = filters?.skip || 0;
    
    // Tentar usar endpoint específico de reviews primeiro
    try {
      const response = await apiClient.get(`/admin/places/${placeId}/reviews?limit=${limit}&skip=${skip}`);
      return response.data;
    } catch (error) {
      // Fallback para o endpoint do dashboard se o específico não existir
      console.warn('Endpoint específico de reviews não encontrado, usando dashboard:', error);
      const response = await apiClient.get(`/admin/places/${placeId}/dashboard/recent-reviews?limit=${limit}&skip=${skip}`);
      return response.data;
    }
  },

  // Buscar estatísticas das avaliações (usando endpoint do dashboard)
  getReviewStats: async (placeId: string) => {
    const response = await apiClient.get(`/admin/places/${placeId}/dashboard/stats`);
    return response.data;
  },

  // TODO: Implementar endpoints de gerenciamento de reviews no backend
  // Deletar avaliação
  deleteReview: async (_placeId: string, _reviewId: string) => {
    throw new Error('Endpoint de deletar review não implementado no backend');
    // await apiClient.delete(`/admin/places/${placeId}/reviews/${reviewId}`);
  },

  // Responder avaliação
  respondToReview: async (_placeId: string, _reviewId: string, _response: string) => {
    throw new Error('Endpoint de responder review não implementado no backend');
    // const result = await apiClient.post(`/admin/places/${placeId}/reviews/${reviewId}/respond`, {
    //   response,
    // });
    // return result.data;
  },

  // Moderar avaliação
  moderateReview: async (_placeId: string, _reviewId: string, _action: 'approve' | 'reject' | 'flag', _reason?: string) => {
    throw new Error('Endpoint de moderar review não implementado no backend');
    // const result = await apiClient.post(`/admin/places/${placeId}/reviews/${reviewId}/moderate`, {
    //   action,
    //   reason,
    // });
    // return result.data;
  },
};

// Método para upload de imagens
export const imageService = {
  uploadImage: async (formData: FormData): Promise<{ url: string }> => {
    const response = await apiClient.post('/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    return response.data;
  },
};

// Métodos para dashboard
export const dashboardService = {
  // Buscar dados do dashboard
  getDashboardData: async (placeId: string) => {
    const response = await apiClient.get(`/admin/places/${placeId}/dashboard`);
    return response.data;
  },

  // Buscar estatísticas
  getStats: async (placeId: string) => {
    const response = await apiClient.get(`/admin/places/${placeId}/dashboard/stats`);
    return response.data;
  },

  // Buscar avaliações recentes
  getRecentReviews: async (placeId: string, limit: number = 5) => {
    const response = await apiClient.get(`/admin/places/${placeId}/dashboard/recent-reviews?limit=${limit}`);
    return response.data;
  },

  // Buscar produtos populares
  getPopularProducts: async (placeId: string, limit: number = 5) => {
    const response = await apiClient.get(`/admin/places/${placeId}/dashboard/popular-products?limit=${limit}`);
    return response.data;
  },
};

// Métodos para configurações da conta
export const accountService = {
  // Buscar dados da conta do BusinessUser logado
  getProfile: async (): Promise<BusinessUser> => {
    const response = await apiClient.get('/admin/account-settings/profile');
    return response.data;
  },

  // Atualizar dados da conta
  updateProfile: async (data: UpdateBusinessUserRequest): Promise<BusinessUser> => {
    const response = await apiClient.put('/admin/account-settings/profile', data);
    return response.data;
  },

  // Buscar informações básicas da conta
  getAccountInfo: async () => {
    const response = await apiClient.get('/admin/account-settings/info');
    return response.data;
  },
};

// Métodos para informações empresariais do estabelecimento
export const placeBusinessService = {
  // Buscar dados empresariais do Place atual
  getBusinessInfo: async (placeId: string): Promise<PlaceBusinessInfo> => {
    const response = await apiClient.get(`/admin/places/${placeId}/business-info`);
    return response.data;
  },

  // Atualizar dados empresariais do Place
  updateBusinessInfo: async (placeId: string, data: UpdatePlaceBusinessInfoRequest): Promise<PlaceBusinessInfo> => {
    const response = await apiClient.put(`/admin/places/${placeId}/business-info`, data);
    return response.data;
  },
};

// Métodos para gerenciamento de categorias (Super Admin)
export const categoryService = {
  // Listar todas as categorias (incluindo inativas)
  getAllCategories: async (): Promise<Category[]> => {
    const response = await apiClient.get('/categories/all');
    return response.data;
  },

  // Criar categoria
  createCategory: async (data: CreateCategoryDto): Promise<Category> => {
    const response = await apiClient.post('/categories', data);
    return response.data;
  },

  // Atualizar categoria
  updateCategory: async (id: string, data: UpdateCategoryDto): Promise<Category> => {
    const response = await apiClient.put(`/categories/${id}`, data);
    return response.data;
  },

  // Deletar categoria
  deleteCategory: async (id: string): Promise<void> => {
    await apiClient.delete(`/categories/${id}`);
  },
};

export const coffeeProfileLevelService = {
  getAllCoffeeProfileLevels: async (): Promise<CoffeeProfileLevel[]> => {
    const response = await apiClient.get('/coffee-profile-levels/all');
    return response.data;
  },

  createCoffeeProfileLevel: async (data: CreateCoffeeProfileLevelDto): Promise<CoffeeProfileLevel> => {
    const response = await apiClient.post('/coffee-profile-levels', data);
    return response.data;
  },

  updateCoffeeProfileLevel: async (id: string, data: UpdateCoffeeProfileLevelDto): Promise<CoffeeProfileLevel> => {
    const response = await apiClient.put(`/coffee-profile-levels/${id}`, data);
    return response.data;
  },

  deleteCoffeeProfileLevel: async (id: string): Promise<void> => {
    await apiClient.delete(`/coffee-profile-levels/${id}`);
  },
};

// Métodos para gerenciamento de características (Super Admin)
export const characteristicService = {
  // Listar todas as características (incluindo inativas)
  getAllCharacteristics: async (): Promise<Characteristic[]> => {
    const response = await apiClient.get('/characteristics/all');
    return response.data;
  },

  // Criar característica
  createCharacteristic: async (data: CreateCharacteristicDto): Promise<Characteristic> => {
    const response = await apiClient.post('/characteristics', data);
    return response.data;
  },

  // Atualizar característica
  updateCharacteristic: async (id: string, data: UpdateCharacteristicDto): Promise<Characteristic> => {
    const response = await apiClient.put(`/characteristics/${id}`, data);
    return response.data;
  },

  // Deletar característica
  deleteCharacteristic: async (id: string): Promise<void> => {
    await apiClient.delete(`/characteristics/${id}`);
  },
};

// Métodos para gerenciamento de cidades
export const cityService = {
  // Listar todas as cidades
  getAllCities: async (): Promise<City[]> => {
    const response = await apiClient.get('/places/cities');
    return response.data;
  },

  // Buscar cidades por estado
  getCitiesByState: async (stateId: string): Promise<City[]> => {
    const response = await apiClient.get(`/places/cities/state/${stateId}`);
    return response.data;
  },
};

export default apiClient; 
